import { UTLoading, UTBox, UTTypography } from '@group-link-one/gl-ui/utilities';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserProfile, getUserRoles } from '../UserManagement/Services';
import type { UserProfile, UserRoles } from '../UserManagement/Types/UserManagement.types';
import { UserOverviewContent } from './Components/UserOverviewContent';

const UserOverview = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [roles, setRoles] = useState<UserRoles | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) {
        setError('User ID is required');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const [profileData, rolesData] = await Promise.all([
          getUserProfile(userId),
          getUserRoles(userId),
        ]);

        setProfile(profileData);
        setRoles(rolesData);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch user data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch user data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  if (isLoading) {
    return <UTLoading screen='as-full-screen' type='dots' />;
  }

  if (error || !profile || !userId) {
    return (
      <UTBox
        flexDirection='column'
        gap='12px'
        padding='40px'
        alignItems='center'
        justifyContent='center'
      >
        <UTTypography fontSize='16px' fontWeight='600' color='#dc2626'>
          {error || 'User not found'}
        </UTTypography>
        <button onClick={() => navigate('/settings/users')}>Back to User Management</button>
      </UTBox>
    );
  }

  return (
    <UserOverviewContent
      profile={profile}
      roles={roles}
      onBack={() => navigate('/settings/users')}
      onRefresh={async () => {
        try {
          const profileData = await getUserProfile(userId);
          const rolesData = await getUserRoles(userId);
          setProfile(profileData);
          setRoles(rolesData);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Failed to refresh user data:', err);
        }
      }}
    />
  );
};

export { UserOverview };
